import React, {
	Children,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { ReactComponent as ArrowLeft } from 'assets/icons/chevron-left.svg'
import { useMediaQuery } from 'react-responsive'
import s from './FinderCardsSlider.module.scss'

const CARD_WIDTH = 300
const GAP = 14

/**
 * Horizontal finder cards with desktop progress bar and mobile dots
 * (student CareerScreen parity, counselor theme).
 */
export default function FinderCardsSlider({ children, cardWidth = CARD_WIDTH }) {
	const items = useMemo(() => Children.toArray(children).filter(Boolean), [children])
	const isDesktop = useMediaQuery({ query: '(min-width: 991px)' })
	const rootRef = useRef(null)
	const trackRef = useRef(null)
	const [progress, setProgress] = useState(0)
	const [showProgressBar, setShowProgressBar] = useState(false)
	const [activeIndex, setActiveIndex] = useState(0)
	const [canPrev, setCanPrev] = useState(false)
	const [canNext, setCanNext] = useState(false)

	const sync = useCallback(() => {
		const track = trackRef.current
		if (!track) return
		const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0)
		const left = track.scrollLeft
		const step = cardWidth + GAP
		const index = step > 0 ? Math.round(left / step) : 0
		setActiveIndex(Math.min(Math.max(index, 0), Math.max(items.length - 1, 0)))
		setCanPrev(left > 4)
		setCanNext(left < maxScroll - 4)
		setShowProgressBar(maxScroll > 8 && items.length > 0)
		if (maxScroll <= 0) {
			setProgress(100)
			return
		}
		const visibleRatio = track.clientWidth / track.scrollWidth
		const scrolledRatio = left / maxScroll
		setProgress(Math.min(100, (visibleRatio + scrolledRatio * (1 - visibleRatio)) * 100))
	}, [cardWidth, items.length])

	useEffect(() => {
		sync()
		const track = trackRef.current
		if (!track) return undefined
		const onScroll = () => sync()
		track.addEventListener('scroll', onScroll, { passive: true })
		window.addEventListener('resize', sync)
		return () => {
			track.removeEventListener('scroll', onScroll)
			window.removeEventListener('resize', sync)
		}
	}, [sync, items.length])

	const scrollByDir = dir => {
		const track = trackRef.current
		if (!track) return
		track.scrollBy({ left: dir * (cardWidth + GAP), behavior: 'smooth' })
	}

	const scrollToIndex = index => {
		const track = trackRef.current
		if (!track) return
		track.scrollTo({ left: index * (cardWidth + GAP), behavior: 'smooth' })
	}

	if (!items.length) return null

	return (
		<div className={s.root} ref={rootRef}>
			<div className={s.viewport}>
				{canPrev ? (
					<button
						type="button"
						className={`${s.arrow} ${s.arrowPrev}`}
						aria-label="Previous"
						onClick={() => scrollByDir(-1)}
					>
						<ArrowLeft />
					</button>
				) : null}
				{canNext ? (
					<button
						type="button"
						className={`${s.arrow} ${s.arrowNext}`}
						aria-label="Next"
						onClick={() => scrollByDir(1)}
					>
						<ArrowLeft className={s.arrowIconFlip} />
					</button>
				) : null}

				<div className={s.track} ref={trackRef}>
					{items.map((child, index) => (
						<div
							className={s.slide}
							key={child.key || index}
							style={{ width: cardWidth }}
						>
							{child}
						</div>
					))}
				</div>
			</div>

			{isDesktop && showProgressBar ? (
				<div className={s.progressBar}>
					<div className={s.progressFill} style={{ maxWidth: `${progress}%` }} />
				</div>
			) : null}

			{!isDesktop && items.length > 1 ? (
				<div className={s.dots}>
					<div className={s.dotsInner}>
						{items.map((child, index) => (
							<button
								key={child.key || index}
								type="button"
								className={`${s.dot} ${index === activeIndex ? s.dotActive : ''}`}
								aria-label={`Go to card ${index + 1}`}
								onClick={() => scrollToIndex(index)}
							/>
						))}
					</div>
				</div>
			) : null}
		</div>
	)
}
